import React from 'react';
import type { PlaybookDiff } from '../types/playbook.types';

interface VersionDiffPanelProps {
  diff: PlaybookDiff;
  onClose: () => void;
}

const CHANGE_BADGE: Record<string, string> = {
  added: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  removed: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
  modified: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
};

const VersionDiffPanel: React.FC<VersionDiffPanelProps> = ({ diff, onClose }) => {
  const s = diff.summary;

  return (
    <div className="space-y-4" style={{ animation: 'coverageDrillDown 250ms ease-out' }}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
          Version Diff: v{diff.sourceVersion} → v{diff.targetVersion}
        </h4>
        <button
          type="button"
          onClick={onClose}
          className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          Close
        </button>
      </div>

      {/* Summary stat cards */}
      <div className="flex flex-wrap gap-2">
        {s.statusesAdded > 0 && (
          <span className="rounded-md bg-emerald-50 px-2 py-1 text-[10px] font-medium text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300">
            {s.statusesAdded} statuses added
          </span>
        )}
        {s.statusesRemoved > 0 && (
          <span className="rounded-md bg-rose-50 px-2 py-1 text-[10px] font-medium text-rose-700 dark:bg-rose-900/20 dark:text-rose-300">
            {s.statusesRemoved} statuses removed
          </span>
        )}
        {s.statusesModified > 0 && (
          <span className="rounded-md bg-amber-50 px-2 py-1 text-[10px] font-medium text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
            {s.statusesModified} statuses modified
          </span>
        )}
        {s.transitionsModified > 0 && (
          <span className="rounded-md bg-amber-50 px-2 py-1 text-[10px] font-medium text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
            {s.transitionsModified} transitions modified
          </span>
        )}
        {s.guardsChanged > 0 && (
          <span className="rounded-md bg-lex-accent-soft px-2 py-1 text-[10px] font-medium text-lex-accent">
            {s.guardsChanged} guards changed
          </span>
        )}
      </div>

      {/* Status changes */}
      {diff.statusChanges.length > 0 && (
        <div>
          <h5 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">Status Changes</h5>
          <div className="space-y-1">
            {diff.statusChanges.map((sc) => (
              <div key={sc.slug} className="flex items-center gap-2 py-1 px-2 rounded bg-gray-50 dark:bg-gray-800/50">
                <span className={`rounded px-1.5 py-0.5 text-[9px] font-medium ${CHANGE_BADGE[sc.changeType]}`}>
                  {sc.changeType}
                </span>
                <span className="text-xs font-medium text-gray-900 dark:text-white">{sc.slug}</span>
                {sc.details && (
                  <span className="text-[10px] text-gray-500 dark:text-gray-400">{sc.details}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Transition changes */}
      {diff.transitionChanges.length > 0 && (
        <div>
          <h5 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">Transition Changes</h5>
          <div className="space-y-2">
            {diff.transitionChanges.map((tc) => (
              <TransitionDiffAccordion key={tc.slug} change={tc} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

function TransitionDiffAccordion({ change }: { change: VersionDiffPanelProps['diff']['transitionChanges'][0] }) {
  const [expanded, setExpanded] = React.useState(false);
  const hasSubDiffs = (change.guardChanges?.length || 0) + (change.validatorChanges?.length || 0) + (change.actionChanges?.length || 0) > 0;

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-800"
      >
        <span className={`rounded px-1.5 py-0.5 text-[9px] font-medium ${CHANGE_BADGE[change.changeType]}`}>
          {change.changeType}
        </span>
        <span className="text-xs font-medium text-gray-900 dark:text-white">{change.name}</span>
        <span className="text-[10px] text-gray-400">{change.slug}</span>
        {hasSubDiffs && (
          <span className="ml-auto text-[10px] text-gray-400">{expanded ? '▲' : '▼'}</span>
        )}
      </button>
      {expanded && hasSubDiffs && (
        <div className="px-3 pb-2 space-y-1 border-t border-gray-100 dark:border-gray-700 pt-2">
          {change.guardChanges?.map((g, i) => (
            <div key={i} className="text-[10px] text-gray-600 dark:text-gray-400">
              <span className={`font-medium ${g.changeType === 'added' ? 'text-emerald-600' : g.changeType === 'removed' ? 'text-rose-600' : 'text-amber-600'}`}>
                {g.changeType === 'added' ? '+' : g.changeType === 'removed' ? '-' : '~'}
              </span>{' '}
              Guard: {g.guardType} {g.detail && `— ${g.detail}`}
            </div>
          ))}
          {change.validatorChanges?.map((v, i) => (
            <div key={i} className="text-[10px] text-gray-600 dark:text-gray-400">
              <span className={`font-medium ${v.changeType === 'added' ? 'text-emerald-600' : v.changeType === 'removed' ? 'text-rose-600' : 'text-amber-600'}`}>
                {v.changeType === 'added' ? '+' : v.changeType === 'removed' ? '-' : '~'}
              </span>{' '}
              Validator: {v.validatorType} {v.detail && `— ${v.detail}`}
            </div>
          ))}
          {change.actionChanges?.map((a, i) => (
            <div key={i} className="text-[10px] text-gray-600 dark:text-gray-400">
              <span className={`font-medium ${a.changeType === 'added' ? 'text-emerald-600' : a.changeType === 'removed' ? 'text-rose-600' : 'text-amber-600'}`}>
                {a.changeType === 'added' ? '+' : a.changeType === 'removed' ? '-' : '~'}
              </span>{' '}
              Action: {a.actionType} {a.detail && `— ${a.detail}`}
            </div>
          ))}
          {change.configChanges && Object.keys(change.configChanges).length > 0 && (
            <div className="mt-1 rounded bg-gray-100 dark:bg-gray-700 p-2 font-mono text-[9px]">
              {Object.entries(change.configChanges).map(([key, val]) => (
                <div key={key} className="text-amber-700 dark:text-amber-300">
                  {key}: <span className="text-rose-500 line-through">{JSON.stringify(val.old)}</span>
                  {' → '}<span className="text-emerald-600">{JSON.stringify(val.new)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default VersionDiffPanel;
